import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, 
  Trophy, 
  GraduationCap, 
  BookOpen, 
  Image, 
  TrendingUp,
  Calendar,
  Eye,
  Users,
  Heart,
  ArrowRight
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAnalyticsData, TrafficOverviewSummary } from '../../hooks/useAnalytics';

interface DashboardStats {
  totalProfiles: number;
  totalAchievements: number;
  totalEducation: number;
  totalTutoring: number;
  totalGallery: number;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => (
  <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`p-3 rounded-lg ${color} shadow-sm`}>
            {icon}
          </div>
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate mb-1">{title}</dt>
            <dd className="flex items-baseline">
              <div className="text-2xl font-bold text-gray-900">{value}</div>
              {trend && (
                <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                  <TrendingUp className="self-center flex-shrink-0 h-4 w-4 text-green-500" />
                  <span className="sr-only">Increased by</span>
                  {trend}
                </div>
              )}
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export const AdminDashboard: React.FC = () => {
  const { fetchMonthlyTraffic } = useAnalyticsData();
  const [traffic, setTraffic] = useState<TrafficOverviewSummary | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalProfiles: 0,
    totalAchievements: 0,
    totalEducation: 0,
    totalTutoring: 0,
    totalGallery: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch counts from all tables and traffic stats concurrently
      const [profilesResult, achievementsResult, educationResult, tutoringResult, galleryResult, trafficResult] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('achievements').select('*', { count: 'exact', head: true }),
        supabase.from('education').select('*', { count: 'exact', head: true }),
        supabase.from('tutoring').select('*', { count: 'exact', head: true }),
        supabase.from('gallery').select('*', { count: 'exact', head: true }),
        fetchMonthlyTraffic().catch(() => null)
      ]);

      setStats({
        totalProfiles: profilesResult.count || 0,
        totalAchievements: achievementsResult.count || 0,
        totalEducation: educationResult.count || 0,
        totalTutoring: tutoringResult.count || 0,
        totalGallery: galleryResult.count || 0
      });

      if (trafficResult) {
        setTraffic(trafficResult);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Gagal memuat statistik dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Profil',
      value: stats.totalProfiles,
      icon: <User className="h-6 w-6 text-white" />,
      color: 'bg-blue-500'
    },
    {
      title: 'Pencapaian',
      value: stats.totalAchievements,
      icon: <Trophy className="h-6 w-6 text-white" />,
      color: 'bg-yellow-500'
    },
    {
      title: 'Riwayat Pendidikan',
      value: stats.totalEducation,
      icon: <GraduationCap className="h-6 w-6 text-white" />,
      color: 'bg-green-500'
    },
    {
      title: 'Tempat Les',
      value: stats.totalTutoring,
      icon: <BookOpen className="h-6 w-6 text-white" />,
      color: 'bg-purple-500'
    },
    {
      title: 'Foto Galeri',
      value: stats.totalGallery,
      icon: <Image className="h-6 w-6 text-white" />,
      color: 'bg-pink-500'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Memuat data dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statCards.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
          />
        ))}
      </div>

      {/* Traffic & Pengunjung Overview */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 rounded-2xl p-5 sm:p-6 text-white shadow-sm border border-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-sky-400" />
              <h3 className="text-base sm:text-lg font-bold">Ringkasan Traffic & Pengunjung</h3>
            </div>
            <p className="text-xs text-slate-300 mt-1">
              Data frekuensi kunjungan website, pengunjung unik per device, dan total dukungan.
            </p>
          </div>
          <Link
            to="/admin/analytics"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-xs font-semibold text-white transition-all active:scale-95 self-start sm:self-auto cursor-pointer"
          >
            <span>Buka Laporan Bulanan Lengkap</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-5">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 sm:p-4">
            <div className="flex items-center justify-between text-slate-300 text-xs mb-1">
              <span>Total Kunjungan</span>
              <Eye className="h-4 w-4 text-sky-400" />
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-white">
              {traffic?.allTimeViews.toLocaleString() || 0}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Semua waktu</div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 sm:p-4">
            <div className="flex items-center justify-between text-slate-300 text-xs mb-1">
              <span>Pengunjung Unik</span>
              <Users className="h-4 w-4 text-purple-400" />
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-white">
              {traffic?.allTimeUniqueVisitors.toLocaleString() || 0}
            </div>
            <div className="text-[11px] text-purple-300 mt-0.5">Device berbeda</div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 sm:p-4">
            <div className="flex items-center justify-between text-slate-300 text-xs mb-1">
              <span>Bulan Berjalan</span>
              <Calendar className="h-4 w-4 text-emerald-400" />
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-white">
              {traffic?.thisMonthViews.toLocaleString() || 0}
            </div>
            <div className="text-[11px] text-emerald-300 mt-0.5">
              {traffic?.thisMonthUniqueVisitors || 0} pengunjung unik
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 sm:p-4">
            <div className="flex items-center justify-between text-slate-300 text-xs mb-1">
              <span>Total Dukungan</span>
              <Heart className="h-4 w-4 text-rose-400 fill-rose-400" />
            </div>
            <div className="text-xl sm:text-2xl font-extrabold text-white">
              {traffic?.totalLikes.toLocaleString() || 0}
            </div>
            <div className="text-[11px] text-rose-300 mt-0.5">Likes pengunjung</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Aksi Cepat</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <button
              onClick={() => window.location.href = '/admin/profile'}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all duration-200 group"
            >
              <User className="h-8 w-8 text-blue-500 group-hover:text-blue-600" />
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-gray-900 group-hover:text-gray-800">Edit Profil</p>
                <p className="text-sm text-gray-500">Kelola informasi profil</p>
              </div>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/achievements'}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-yellow-300 transition-all duration-200 group"
            >
              <Trophy className="h-8 w-8 text-yellow-500 group-hover:text-yellow-600" />
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-gray-900 group-hover:text-gray-800">Tambah Pencapaian</p>
                <p className="text-sm text-gray-500">Catat prestasi baru</p>
              </div>
            </button>
            
            <button
              onClick={() => window.location.href = '/admin/gallery'}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-pink-300 transition-all duration-200 group"
            >
              <Image className="h-8 w-8 text-pink-500 group-hover:text-pink-600" />
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-gray-900 group-hover:text-gray-800">Upload Foto</p>
                <p className="text-sm text-gray-500">Tambah ke galeri</p>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/admin/analytics'}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 group"
            >
              <TrendingUp className="h-8 w-8 text-purple-500 group-hover:text-purple-600" />
              <div className="ml-3 text-left">
                <p className="text-sm font-medium text-gray-900 group-hover:text-gray-800">Traffic & Pengunjung</p>
                <p className="text-sm text-gray-500">Lihat statistik bulanan</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Aktivitas Terbaru</h3>
        </div>
        <div className="p-6">
          <div className="text-center text-gray-500 py-8">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">Belum ada aktivitas terbaru</p>
            <p className="text-sm mt-1">Mulai mengelola konten untuk melihat aktivitas di sini</p>
          </div>
        </div>
      </div>
    </div>
  );
};